import { useCallback, useEffect } from "react";
import useSWR, { useSWRConfig } from "swr";
import useSWRInfinite from "swr/infinite";
import type {
	Notification,
	NotificationApi,
	NotificationFeedResponse,
	NotificationUnreadCountResponse,
} from "types/notification.types";
import { api } from "utils/axios";
import { resolveNotificationLink } from "utils/notificationLinks";
import {
	connectToWebSocket,
	GATEWAY_TYPES,
	offSocketEvent,
	onSocketEvent,
} from "utils/websocket";

const FEED_KEY = "notifications";
const UNREAD_COUNT_KEY = "notifications/unread-count";

export type NotificationFeedFilter = "all" | "unread";

/**
 * Normalize an API row into the alias-carrying `Notification` the UI consumes.
 * The orphaned NotificationCenter reads `type` / `message` / `date`; the API
 * sends `severity` / `body` / `createdAt`. We keep both in sync here so the
 * component compiles and renders unchanged.
 */
export function toNotification(row: NotificationApi): Notification {
	return {
		...row,
		link: resolveNotificationLink(row.link, row.companyId),
		type: row.severity,
		message: row.body,
		date: row.createdAt,
	};
}

const FEED_PAGE_SIZE = 30;
// How many rows the bell wants loaded. Module-level so every mounted bell and
// every revalidation agree on it.
let feedTarget = FEED_PAGE_SIZE;

type FeedPageFetcher = (key: string) => Promise<NotificationFeedResponse>;

// Page 1 is the plain feed key (API default page size); later pages chain the
// cursor until the target is loaded.
async function fetchFeed(
	fetchPage: FeedPageFetcher,
): Promise<NotificationFeedResponse> {
	const rows: NotificationApi[] = [];
	let cursor: string | null = null;
	let key: string = FEED_KEY;
	do {
		const page: NotificationFeedResponse | undefined = await fetchPage(key);
		rows.push(...(page?.data ?? []));
		cursor = page?.nextCursor ?? null;
		key = `${FEED_KEY}?cursor=${encodeURIComponent(cursor ?? "")}`;
	} while (cursor && rows.length < feedTarget);
	return { data: dedupeById(rows), nextCursor: cursor };
}

export function useNotifications() {
	const { mutate, fetcher } = useSWRConfig();

	const feed = useSWR<NotificationFeedResponse>(FEED_KEY, () =>
		fetchFeed(fetcher as FeedPageFetcher),
	);
	const unread = useSWR<NotificationUnreadCountResponse>(UNREAD_COUNT_KEY);

	const notifications: Notification[] = (feed.data?.data ?? []).map(
		toNotification,
	);
	const unreadCount = unread.data?.count ?? 0;
	const hasMore = feed.data?.nextCursor != null;

	// Raise the target and refetch. The fetcher reloads every page up to the
	// target, so a focus or Refresh revalidation keeps the older rows too, and
	// SWR dedupes a double click into one request.
	const loadMore = useCallback(async () => {
		if (!feed.data?.nextCursor) return;
		feedTarget = Math.max(feedTarget, feed.data.data.length) + FEED_PAGE_SIZE;
		await mutate(FEED_KEY);
	}, [feed.data, mutate]);

	// --- real-time push subscription ------------------------------------------
	useEffect(() => {
		const handleNotification = (row: NotificationApi) => {
			if (!row?.id) return;
			// Prepend the new row to the feed cache (de-duped by id).
			mutate<NotificationFeedResponse>(
				FEED_KEY,
				(current) => {
					const existing = current?.data ?? [];
					if (existing.some((n) => n.id === row.id)) {
						return current ?? { data: existing, nextCursor: null };
					}
					return {
						data: [row, ...existing],
						nextCursor: current?.nextCursor ?? null,
					};
				},
				{ revalidate: false },
			);
		};

		const handleUnreadCount = (payload: { count: number }) => {
			if (typeof payload?.count !== "number") return;
			mutate<NotificationUnreadCountResponse>(
				UNREAD_COUNT_KEY,
				{ count: payload.count },
				{ revalidate: false },
			);
		};

		onSocketEvent(
			"notification",
			handleNotification,
			GATEWAY_TYPES.NOTIFICATION,
		);
		onSocketEvent(
			"notification:unread-count",
			handleUnreadCount,
			GATEWAY_TYPES.NOTIFICATION,
		);

		// Establish the connection (idempotent — SocketManager dedupes).
		connectToWebSocket(GATEWAY_TYPES.NOTIFICATION).catch((err) =>
			console.error("Failed to connect notification socket", err),
		);

		return () => {
			offSocketEvent(
				"notification",
				handleNotification,
				GATEWAY_TYPES.NOTIFICATION,
			);
			offSocketEvent(
				"notification:unread-count",
				handleUnreadCount,
				GATEWAY_TYPES.NOTIFICATION,
			);
		};
	}, [mutate]);

	// --- mutations (optimistic + rollback) ------------------------------------
	const markAsRead = useCallback(
		async (id: string) => {
			const prevFeed = feed.data;
			const prevUnread = unread.data;
			const wasUnread = prevFeed?.data.some((n) => n.id === id && !n.read);

			mutate<NotificationFeedResponse>(
				FEED_KEY,
				(current) =>
					current
						? {
								...current,
								data: current.data.map((n) =>
									n.id === id ? { ...n, read: true } : n,
								),
							}
						: current,
				{ revalidate: false },
			);
			if (wasUnread) {
				mutate<NotificationUnreadCountResponse>(
					UNREAD_COUNT_KEY,
					{ count: Math.max(0, (prevUnread?.count ?? 0) - 1) },
					{ revalidate: false },
				);
			}

			try {
				await api.patch(`notifications/${id}/read`);
			} catch (error) {
				// Roll back both caches on failure.
				mutate(FEED_KEY, prevFeed, { revalidate: false });
				mutate(UNREAD_COUNT_KEY, prevUnread, { revalidate: false });
				throw error;
			}
		},
		[feed.data, unread.data, mutate],
	);

	const markAllAsRead = useCallback(async () => {
		const prevFeed = feed.data;
		const prevUnread = unread.data;

		mutate<NotificationFeedResponse>(
			FEED_KEY,
			(current) =>
				current
					? {
							...current,
							data: current.data.map((n) => ({ ...n, read: true })),
						}
					: current,
			{ revalidate: false },
		);
		mutate<NotificationUnreadCountResponse>(
			UNREAD_COUNT_KEY,
			{ count: 0 },
			{ revalidate: false },
		);

		try {
			await api.post("notifications/read-all");
		} catch (error) {
			mutate(FEED_KEY, prevFeed, { revalidate: false });
			mutate(UNREAD_COUNT_KEY, prevUnread, { revalidate: false });
			throw error;
		}
	}, [feed.data, unread.data, mutate]);

	const dismiss = useCallback(
		async (id: string) => {
			const prevFeed = feed.data;
			const prevUnread = unread.data;
			const wasUnread = prevFeed?.data.some((n) => n.id === id && !n.read);

			mutate<NotificationFeedResponse>(
				FEED_KEY,
				(current) =>
					current
						? {
								...current,
								data: current.data.filter((n) => n.id !== id),
							}
						: current,
				{ revalidate: false },
			);
			if (wasUnread) {
				mutate<NotificationUnreadCountResponse>(
					UNREAD_COUNT_KEY,
					{ count: Math.max(0, (prevUnread?.count ?? 0) - 1) },
					{ revalidate: false },
				);
			}

			try {
				await api.delete(`notifications/${id}`);
			} catch (error) {
				mutate(FEED_KEY, prevFeed, { revalidate: false });
				mutate(UNREAD_COUNT_KEY, prevUnread, { revalidate: false });
				throw error;
			}
		},
		[feed.data, unread.data, mutate],
	);

	const dismissAll = useCallback(async () => {
		const prevFeed = feed.data;
		const prevUnread = unread.data;

		mutate<NotificationFeedResponse>(
			FEED_KEY,
			(current) => (current ? { ...current, data: [] } : current),
			{ revalidate: false },
		);
		mutate<NotificationUnreadCountResponse>(
			UNREAD_COUNT_KEY,
			{ count: 0 },
			{ revalidate: false },
		);

		try {
			await api.delete("notifications");
		} catch (error) {
			mutate(FEED_KEY, prevFeed, { revalidate: false });
			mutate(UNREAD_COUNT_KEY, prevUnread, { revalidate: false });
			throw error;
		}
	}, [feed.data, unread.data, mutate]);

	return {
		notifications,
		unreadCount,
		isLoading: feed.isLoading,
		error: feed.error,
		markAsRead,
		markAllAsRead,
		dismiss,
		dismissAll,
		hasMore,
		loadMore,
		revalidate: async () => {
			await Promise.all([mutate(FEED_KEY), mutate(UNREAD_COUNT_KEY)]);
		},
	};
}

/**
 * Paginated feed hook for the full `/notifications` page. Uses `useSWRInfinite`
 * to fetch successive cursor pages and accumulate them into a single list, and
 * keeps the header bell's `useNotifications()` API entirely untouched.
 *
 * Pages are keyed by cursor (keyset pagination, see design §4): page 0 has no
 * cursor; each subsequent page is keyed off the previous page's `nextCursor`.
 * `loadMore()` bumps `size`; `hasMore` is derived from the last page's
 * `nextCursor`. Mutations write through to BOTH this infinite cache and the
 * bell's flat caches so the badge and drawer stay consistent.
 */
export function useNotificationsFeed(filter: NotificationFeedFilter = "all") {
	const { mutate: globalMutate } = useSWRConfig();

	const unreadQuery = filter === "unread" ? "&unread=true" : "";

	const getKey = (
		index: number,
		previousPage: NotificationFeedResponse | null,
	): string | null => {
		// Reached the end — stop requesting further pages.
		if (previousPage && !previousPage.nextCursor) return null;
		if (index === 0) return `${FEED_KEY}?limit=20${unreadQuery}`;
		return `${FEED_KEY}?limit=20&cursor=${encodeURIComponent(
			previousPage?.nextCursor ?? "",
		)}${unreadQuery}`;
	};

	const { data, error, isLoading, isValidating, size, setSize, mutate } =
		useSWRInfinite<NotificationFeedResponse>(getKey, {
			revalidateFirstPage: false,
		});

	const pages = data ?? [];
	const notifications: Notification[] = dedupeById(
		pages.flatMap((page) => page?.data ?? []),
	).map(toNotification);

	const lastPage = pages[pages.length - 1];
	const hasMore = lastPage ? lastPage.nextCursor !== null : false;
	const isLoadingMore =
		isValidating && size > 0 && typeof pages[size - 1] === "undefined";
	const isEmpty = !isLoading && notifications.length === 0;

	const loadMore = useCallback(() => {
		if (hasMore) setSize((prev) => prev + 1);
	}, [hasMore, setSize]);

	// Optimistically patch a single notification across every loaded page.
	const patchRow = useCallback(
		(id: string, patch: Partial<NotificationApi>) =>
			mutate(
				(current) =>
					current?.map((page) => ({
						...page,
						data: page.data.map((n) => (n.id === id ? { ...n, ...patch } : n)),
					})),
				{ revalidate: false },
			),
		[mutate],
	);

	const removeRow = useCallback(
		(id: string) =>
			mutate(
				(current) =>
					current?.map((page) => ({
						...page,
						data: page.data.filter((n) => n.id !== id),
					})),
				{ revalidate: false },
			),
		[mutate],
	);

	const bumpCount = useCallback(
		(delta: number) =>
			globalMutate<NotificationUnreadCountResponse>(
				UNREAD_COUNT_KEY,
				(current) => ({ count: Math.max(0, (current?.count ?? 0) + delta) }),
				{ revalidate: false },
			),
		[globalMutate],
	);

	const wasUnread = (id: string) =>
		pages.some((page) => page.data.some((n) => n.id === id && !n.read));

	const markAsRead = useCallback(
		async (id: string) => {
			const unread = wasUnread(id);
			patchRow(id, { read: true });
			if (unread) bumpCount(-1);
			try {
				await api.patch(`notifications/${id}/read`);
			} catch (err) {
				mutate();
				globalMutate(UNREAD_COUNT_KEY);
				throw err;
			}
		},
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[patchRow, bumpCount, mutate, globalMutate, pages],
	);

	const markAllAsRead = useCallback(async () => {
		mutate(
			(current) =>
				current?.map((page) => ({
					...page,
					data: page.data.map((n) => ({ ...n, read: true })),
				})),
			{ revalidate: false },
		);
		globalMutate<NotificationUnreadCountResponse>(
			UNREAD_COUNT_KEY,
			{ count: 0 },
			{ revalidate: false },
		);
		try {
			await api.post("notifications/read-all");
		} catch (err) {
			mutate();
			globalMutate(UNREAD_COUNT_KEY);
			throw err;
		}
	}, [mutate, globalMutate]);

	const dismiss = useCallback(
		async (id: string) => {
			const unread = wasUnread(id);
			removeRow(id);
			if (unread) bumpCount(-1);
			try {
				await api.delete(`notifications/${id}`);
			} catch (err) {
				mutate();
				globalMutate(UNREAD_COUNT_KEY);
				throw err;
			}
		},
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[removeRow, bumpCount, mutate, globalMutate, pages],
	);

	return {
		notifications,
		isLoading,
		isLoadingMore,
		isEmpty,
		hasMore,
		error,
		loadMore,
		markAsRead,
		markAllAsRead,
		dismiss,
		revalidate: () => {
			mutate();
			globalMutate(UNREAD_COUNT_KEY);
		},
	};
}

function dedupeById(rows: NotificationApi[]): NotificationApi[] {
	const seen = new Set<string>();
	const out: NotificationApi[] = [];
	for (const row of rows) {
		if (seen.has(row.id)) continue;
		seen.add(row.id);
		out.push(row);
	}
	return out;
}
